import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { getPageSEO } from '../lib/seo-config';
import { 
  TrendingUp, 
  FileCheck, 
  AlertTriangle, 
  Users, 
  ArrowRight,
  BarChart3,
  Clock,
  CheckCircle,
  XCircle,
  Upload,

  Target,
  Zap,
  Play,
  HelpCircle,
  Award,
  Star,
  Eye,
  TrendingDown,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { OnboardingModal } from '../components/OnboardingModal';
import { RecentAnalysesWidget } from '../components/widgets/RecentAnalysesWidget';
import { EmailVerificationBanner } from '../components/widgets/EmailVerificationBanner';
import { PaymentCompletionBanner } from '../components/widgets/PaymentCompletionBanner';
import { DataExplainer, DATA_EXPLAINERS } from '../components/widgets/DataExplainer';
import { TutorialTooltip } from '../components/TutorialTooltip';
import { RecentAnalysisItem } from '../lib/recent-analyses';
import { ProjectService, ProjectStats } from '../lib/projects';
import { UnifiedCreditService } from '../lib/unified-credit-service';



const tutorialSteps = [
  {
    id: 'analysis',
    title: 'Start Your First Analysis',
    description: 'Click Analysis to upload your creative and get instant compliance feedback for all major platforms.',
    target: 'nav button[data-tutorial="analysis"]',
    position: 'right'
  },
  {
    id: 'projects',
    title: 'Organize with Projects',
    description: 'Use Projects to organize your creatives and collaborate with your team members.',
    target: 'nav button[data-tutorial="projects"]',
    position: 'right'
  },
  {
    id: 'reports',
    title: 'Track Your Performance',
    description: 'View detailed reports and analytics to optimize your creative performance.',
    target: 'nav button[data-tutorial="reports"]',
    position: 'right'
  }
];

export function Dashboard() {
  const { user, session, hasTutorialCompleted, markTutorialCompleted, hasOnboardingCompleted } = useAuth();
  const { startOnboarding, hasCompletedInSession } = useOnboarding();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showTutorial, setShowTutorial] = useState(false);
  const [currentTutorialStep, setCurrentTutorialStep] = useState(0);
  
  // Dashboard data state
  const [dashboardStats, setDashboardStats] = useState<ProjectStats | null>(null);
  const [creditData, setCreditData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load dashboard data
  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
    }
  }, [user?.id]);

  const loadDashboardData = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Load both dashboard stats and unified credit data in parallel
      const [stats, credits] = await Promise.all([
        ProjectService.getDashboardStats(),
        UnifiedCreditService.getCreditData(user.id)
      ]);
      
      setDashboardStats(stats);
      setCreditData(credits);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if tutorial should be shown - but only after dashboard is loaded
    const tutorial = searchParams.get('tutorial');
    const payment = searchParams.get('payment');
    const onboarding = searchParams.get('onboarding');
    
    // Only show tutorial on desktop (screen width >= 1024px) since it targets sidebar elements
    const isDesktop = window.innerWidth >= 1024;
    
    if (tutorial === 'true' && !hasTutorialCompleted() && !loading && isDesktop) {
      // Add a delay to ensure all animations and rendering are complete (especially Quick Actions)
      setTimeout(() => {
        setShowTutorial(true);
      }, 1000); // Increased delay to wait for Quick Actions animation (0.7s + buffer)
    } else if (tutorial === 'true' && !isDesktop) {
      // On mobile, just mark tutorial as completed and remove parameter
      markTutorialCompleted();
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('tutorial');
      setSearchParams(newSearchParams);
    }

    // Check if onboarding modal should be shown
    if (onboarding === 'true' && !loading) {
      // Remove the parameter from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('onboarding');
      setSearchParams(newSearchParams);
      
      // Start the onboarding
      setTimeout(() => {
        startOnboarding();
      }, 500); // Small delay to ensure everything is loaded
    }

    // Auto-trigger onboarding for new users (first time on dashboard)
    if (!loading && user && !hasOnboardingCompleted() && !onboarding && !tutorial && !hasCompletedInSession()) {
      // Only show onboarding if user hasn't completed it and it's not explicitly triggered by URL and hasn't been completed in this session
      setTimeout(() => {
        startOnboarding();
      }, 1000); // Delay to ensure dashboard is fully loaded
    }

    // If there's a payment=pending parameter, ensure the PaymentCompletionBanner is visible
    if (payment === 'pending') {
      // Remove the parameter from URL after processing
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('payment');
      setSearchParams(newSearchParams);
    }
  }, [searchParams, hasTutorialCompleted, hasOnboardingCompleted, hasCompletedInSession, loading, setSearchParams, startOnboarding, user]);



  const handleHistoryClick = (analysis: any) => {
    // Navigate to the analysis page with the specific analysis ID
    navigate(`/dashboard/analysis?id=${analysis.id}`);
  };

  const handleNextTutorialStep = async () => {
    if (currentTutorialStep < tutorialSteps.length - 1) {
      setCurrentTutorialStep(currentTutorialStep + 1);
    } else {
      // Tutorial completed
      setShowTutorial(false);
      await markTutorialCompleted();
      // Remove tutorial parameter from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('tutorial');
      setSearchParams(newSearchParams);
    }
  };

  const handlePrevTutorialStep = () => {
    if (currentTutorialStep > 0) {
      setCurrentTutorialStep(currentTutorialStep - 1);
    }
  };

  const handleSkipTutorial = async () => {
    setShowTutorial(false);
    await markTutorialCompleted();
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('tutorial');
    setSearchParams(newSearchParams);
  };



  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <SEOHead {...getPageSEO('/dashboard')} />

      {/* Shutdown Notice */}
      <div className="mb-6 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 flex items-start gap-3">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        <p className="text-amber-300 text-sm">
          <span className="font-semibold">Rulevia is shutting down on July 14, 2026.</span>{' '}
          Thank you for your trust. Your content will remain accessible until that date.
        </p>
      </div>

      {/* Tutorial Overlay */}
      <AnimatePresence>
        {showTutorial && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-30 z-[9998] pointer-events-none"
          />
        )}
      </AnimatePresence>

      {/* Tutorial Tooltip */}
      {showTutorial && currentTutorialStep < tutorialSteps.length && (
        <TutorialTooltip 
          step={tutorialSteps[currentTutorialStep]}
          showTutorial={showTutorial}
          currentTutorialStep={currentTutorialStep}
          tutorialSteps={tutorialSteps}
          onNext={handleNextTutorialStep}
          onPrev={handlePrevTutorialStep}
          onSkip={handleSkipTutorial}
        />
      )}

      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Welcome back, {user?.name?.split(' ')[0]}! 👋
              </h1>
              <p className="text-gray-600 text-sm sm:text-base">
                Here's what's happening with your creative campaigns today.
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              <button
                onClick={loadDashboardData}
                disabled={loading}
                className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 min-w-[44px] min-h-[44px]"
                title="Refresh Dashboard"
              >
                <RefreshCw className={`w-4 h-4 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
                <span className="text-gray-700 hidden md:inline">Refresh</span>
              </button>
              
              <button
                onClick={() => {
                  // Start onboarding modal instead of tutorial
                  setTimeout(() => {
                    startOnboarding();
                  }, 100);
                }}
                className="hidden lg:flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-w-[44px] min-h-[44px]"
              >
                <HelpCircle className="w-4 h-4 text-gray-600" />
                <span className="text-gray-700">Start Tour</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Email Verification Banner */}
      <EmailVerificationBanner 
        userEmail={user?.email}
        isEmailVerified={session?.user?.email_confirmed_at != null}
      />

      {/* Payment Completion Banner */}
      {user?.id && (
        <PaymentCompletionBanner 
          userId={user.id}
          onSubscriptionComplete={(planId) => {
            console.log(`Subscription ${planId} completed successfully`);
            // Optionally refresh dashboard data or show success message
            loadDashboardData();
          }}
        />
      )}

      {/* Error Banner */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <XCircle className="w-5 h-5 text-red-600" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Dashboard Error</h3>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
            <button
              onClick={loadDashboardData}
              className="text-red-600 hover:text-red-700 font-medium text-sm"
            >
              Try Again
            </button>
          </div>
        </motion.div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <FileCheck className="w-6 h-6 text-green-600" />
            </div>
            {!loading && dashboardStats && typeof dashboardStats.launchReadyCreativesTrend === 'number' ? (
              <div className={`flex items-center text-sm ${dashboardStats.launchReadyCreativesTrend > 0 ? 'text-green-600' : dashboardStats.launchReadyCreativesTrend < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                {dashboardStats.launchReadyCreativesTrend > 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : 
                 dashboardStats.launchReadyCreativesTrend < 0 ? <TrendingDown className="w-4 h-4 mr-1" /> : 
                 <span className="w-4 h-4 mr-1 text-center">→</span>}
                {dashboardStats.launchReadyCreativesTrend > 0 ? '+' : ''}{dashboardStats.launchReadyCreativesTrend}%
              </div>
            ) : (
              <span className="text-xs text-gray-400">{loading ? '...' : 'No trend'}</span>
            )}
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
            {loading ? '...' : dashboardStats?.launchReadyCreatives || 0}
          </h3>
          <div className="flex items-center">
            <p className="text-gray-600 text-sm">Launch Ready</p>
            <DataExplainer 
              title={DATA_EXPLAINERS.launchReady.title}
              explanation={DATA_EXPLAINERS.launchReady.explanation}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
            </div>
            <span className="text-yellow-600 text-sm font-medium">
              {loading ? '...' : dashboardStats?.creativesNeedingReview ? `${dashboardStats.creativesNeedingReview}` : '0'}
            </span>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
            {loading ? '...' : dashboardStats?.creativesNeedingReview || 0}
          </h3>
          <p className="text-gray-600 text-sm">Needs Review</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
            {!loading && dashboardStats && typeof dashboardStats.launchReadyRateTrend === 'number' ? (
              <div className={`flex items-center text-sm ${dashboardStats.launchReadyRateTrend > 0 ? 'text-green-600' : dashboardStats.launchReadyRateTrend < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                {dashboardStats.launchReadyRateTrend > 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : 
                 dashboardStats.launchReadyRateTrend < 0 ? <TrendingDown className="w-4 h-4 mr-1" /> : 
                 <span className="w-4 h-4 mr-1 text-center">→</span>}
                {dashboardStats.launchReadyRateTrend > 0 ? '+' : ''}{dashboardStats.launchReadyRateTrend}%
              </div>
            ) : (
              <span className="text-xs text-gray-400">{loading ? '...' : 'No trend'}</span>
            )}
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
            {loading ? '...' : `${dashboardStats?.launchReadyRate || 0}%`}
          </h3>
          <div className="flex items-center">
            <p className="text-gray-600 text-sm">Quality Score</p>
            <DataExplainer 
              title={DATA_EXPLAINERS.overallScore.title}
              explanation={DATA_EXPLAINERS.overallScore.explanation}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <span className="text-purple-600 text-sm font-medium">
              {creditData?.availableCredits || 0}
            </span>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{creditData?.availableCredits || 0}</h3>
          <div className="flex items-center">
            <p className="text-gray-600 text-sm">Credits Remaining</p>
            <DataExplainer 
              title={DATA_EXPLAINERS.creditsUsed.title}
              explanation={DATA_EXPLAINERS.creditsUsed.explanation}
            />
          </div>
        </motion.div>
      </div>

      {/* Performance Overview Widget */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.45 }}
        className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
              <Star className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Performance Overview</h2>
              <p className="text-gray-600 text-sm">AI-powered insights and predictions</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard/reports')}
            className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center space-x-1 transition-colors self-start sm:self-auto"
          >
            <span>View detailed reports</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-6">
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 sm:p-6 border border-purple-200">
            <div className="flex items-center space-x-3 mb-3">
              <Award className="w-8 h-8 text-purple-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Avg Performance</h3>
                <p className="text-sm text-gray-600">Predicted success rate</p>
              </div>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-purple-600 mb-1">
              {loading ? '...' : `${dashboardStats?.averagePerformanceScore || 0}%`}
            </div>
            {!loading && dashboardStats && typeof dashboardStats.averagePerformanceScoreTrend === 'number' ? (
              <div className={`flex items-center text-sm ${dashboardStats.averagePerformanceScoreTrend > 0 ? 'text-green-600' : dashboardStats.averagePerformanceScoreTrend < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                {dashboardStats.averagePerformanceScoreTrend > 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : 
                 dashboardStats.averagePerformanceScoreTrend < 0 ? <TrendingDown className="w-4 h-4 mr-1" /> : 
                 <span className="w-4 h-4 mr-1 text-center">→</span>}
                {dashboardStats.averagePerformanceScoreTrend > 0 ? '+' : ''}{dashboardStats.averagePerformanceScoreTrend}% vs last week
              </div>
            ) : (
              <div className="text-xs text-gray-400">{loading ? '...' : 'No trend data'}</div>
            )}
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
            <div className="flex items-center space-x-3 mb-3">
              <Target className="w-8 h-8 text-blue-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Strongest Projects</h3>
                <p className="text-sm text-gray-600">By project strength</p>
              </div>
            </div>
            <div className="space-y-2">
              {loading ? (
                <div className="text-center text-gray-500 py-4">Loading...</div>
              ) : dashboardStats?.strongestProjects && dashboardStats.strongestProjects.length > 0 ? (
                dashboardStats.strongestProjects.slice(0, 2).map((project, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 truncate">{project.name}</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-bold text-gray-900">{project.strength}%</span>
                      <div className={`flex items-center text-xs ${project.trend > 0 ? 'text-green-600' : project.trend < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                        {project.trend > 0 ? <TrendingUp className="w-3 h-3" /> : 
                         project.trend < 0 ? <TrendingDown className="w-3 h-3" /> : 
                         <span className="w-3 h-3 text-center">→</span>}
                        <span>{project.trend === 0 ? '0' : `${project.trend > 0 ? '+' : ''}${project.trend}`}%</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-4">
                  {dashboardStats ? 'No projects with analyses found' : 'Loading...'}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
            <div className="flex items-center space-x-3 mb-3">
              <Zap className="w-8 h-8 text-yellow-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Top Performers</h3>
                <p className="text-sm text-gray-600">Best creatives</p>
              </div>
            </div>
            <div className="space-y-2">
              {loading ? (
                <div className="text-center text-gray-500 py-4">Loading...</div>
              ) : dashboardStats?.topPerformingCreatives && dashboardStats.topPerformingCreatives.length > 0 ? (
                dashboardStats.topPerformingCreatives.slice(0, 2).map((creative, index) => (
                  <div 
                    key={index} 
                    className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                    onClick={() => creative.analysisId && navigate(`/dashboard/analysis?id=${creative.analysisId}`)}
                  >
                    <img
                      src={creative.thumbnail}
                      alt={creative.name}
                      className="w-8 h-8 rounded object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://images.pexels.com/photos/5632402/pexels-photo-5632402.jpeg?auto=compress&cs=tinysrgb&w=300';
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{creative.name}</div>
                      <div className="text-xs text-gray-500">{creative.project}</div>
                    </div>
                    <div className="text-sm font-bold text-purple-600">{creative.performanceScore}%</div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-4">
                  {dashboardStats ? 'No analyzed creatives found' : 'Loading...'}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="flex items-center justify-center">
          <button
            onClick={() => navigate('/dashboard/reports')}
            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center space-x-2"
          >
            <BarChart3 className="w-4 h-4" />
            <span>View Full Performance Report</span>
          </button>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
        {/* Recent Analysis Widget */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="recent-analyses"
        >
          <RecentAnalysesWidget 
            onViewAll={() => navigate('/dashboard/recent-analyses')}
            onHistoryClick={handleHistoryClick}
          />
        </motion.div>

        {/* Platform Performance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.65 }}
          className="bg-white rounded-xl border border-gray-200 min-h-[500px] flex flex-col"
        >
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <h2 className="text-xl font-semibold text-gray-900">Platform Performance</h2>
                <DataExplainer 
                  title={DATA_EXPLAINERS.platformPerformance.title}
                  explanation={DATA_EXPLAINERS.platformPerformance.explanation}
                />
              </div>
              <BarChart3 className="w-5 h-5 text-gray-400" />
            </div>
          </div>
          <div className="p-4 sm:p-6 space-y-6 flex-1">
            {loading ? (
              <div className="text-center text-gray-500 py-8">Loading platform data...</div>
            ) : (
              dashboardStats?.platformStats.map((platform) => (
                <div key={platform.name}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 ${platform.color} rounded-full`}></div>
                      <span className="font-medium text-gray-900">{platform.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {platform.avgScore}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${platform.avgScore}%` }}
                    ></div>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {platform.total} {platform.total === 1 ? 'analysis' : 'analyses'}
                  </div>
                </div>
              )) || <div className="text-center text-gray-500 py-8">No platform data available</div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.7 }}
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6"
      >
        <div className="bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl p-4 sm:p-6 text-white upload-area">
          <h3 className="text-lg font-semibold mb-2">Start New Analysis</h3>
          <p className="text-purple-100 mb-4 text-sm">Upload your creative and get instant compliance feedback</p>
          <button 
            onClick={() => navigate('/dashboard/analysis')}
            className="bg-white text-purple-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2 min-h-[44px]"
          >
            <Upload className="w-4 h-4" />
            <span>Upload Creative</span>
          </button>
        </div>

        <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Invite Team Members</h3>
          <p className="text-gray-600 mb-4 text-sm">Collaborate with your team on creative projects</p>
          <button 
            onClick={() => navigate('/dashboard/team')}
            className="bg-gray-900 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-800 transition-colors min-h-[44px]"
          >
            Send Invite
          </button>
        </div>

        <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 projects-link">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Create Project</h3>
          <p className="text-gray-600 mb-4 text-sm">Organize your creatives and manage campaigns</p>
          <button 
            onClick={() => navigate('/dashboard/projects')}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors min-h-[44px]"
          >
            New Project
          </button>
        </div>
      </motion.div>

      {/* Onboarding Modal */}
      <OnboardingModal />
    </div>
  );
}